import { createFileRoute, redirect, Link, useNavigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useArticle, useRecordArticleView, useDeleteArticle } from '@/queries/articles';
import { useAuth } from '@/contexts/AuthContext';
import { ArticleViewer } from '@/components/ArticleViewer';
import { ArticleMeta } from '@/components/ArticleMeta';
import { ArticleComments } from '@/components/ArticleComments';
import { ShareArticleModal } from '@/components/ShareArticleModal';
import { RecordingsList } from '@/components/RecordingsList';
import { CircularLoader } from '@/components/CircularLoader';
import { ArticleCollectionBadges } from '@/components/ArticleCollectionBadges';
import { AddArticleToCollectionsModal } from '@/components/AddArticleToCollectionsModal';
import ArrowLeftIcon from '@/assets/common/arrow-left.svg?react';
import ShareIcon from '@/assets/common/share.svg?react';
import styles from './$articleId.module.scss';

export const Route = createFileRoute('/articles/$articleId')({
  component: ArticleDetailPage,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function ArticleDetailPage() {
  const { articleId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const routerState = useRouterState();

  const { data: article, isLoading, error } = useArticle(articleId);
  const recordView = useRecordArticleView();
  const deleteArticle = useDeleteArticle();

  const [showShareModal, setShowShareModal] = useState(false);
  const [showAddToCollectionModal, setShowAddToCollectionModal] = useState(false);

  // Check if we're on a child route (like /edit)
  const isChildRoute = routerState.location.pathname.endsWith('/edit');

  // Record view when article loads
  useEffect(() => {
    if (article && articleId) {
      recordView.mutate(articleId);
    }
  }, [articleId, article?.id]);

  const isOwner = user?.uid === article?.user_id;

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this article? This action cannot be undone.')) {
      deleteArticle.mutate(articleId, {
        onSuccess: () => {
          navigate({ to: '/articles' });
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.articleDetail__loading}>
        <CircularLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.articleDetail__error}>
        <p>Error loading article: {error.message}</p>
        <Link to="/articles">Return to Articles</Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className={styles.articleDetail__notFound}>
        <p>Article not found</p>
        <Link to="/articles">Return to Articles</Link>
      </div>
    );
  }

  // If we're on a child route (like /edit), render the child route component
  if (isChildRoute) {
    return <Outlet />;
  }

  return (
    <div className={styles.articleDetail}>
      <div className={styles.articleDetail__header}>
        <Link to="/articles" className={styles.articleDetail__backButton}>
          <ArrowLeftIcon />
          Back to Articles
        </Link>

        {isOwner && (
          <div className={styles.articleDetail__actions}>
            <motion.button
              onClick={() => setShowShareModal(true)}
              className={styles.articleDetail__actionButton}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ShareIcon />
              Share
            </motion.button>
            <Link
              to="/articles/$articleId/edit"
              params={{ articleId }}
              className={styles.articleDetail__actionButton}
            >
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className={`${styles.articleDetail__actionButton} ${styles['articleDetail__actionButton--danger']}`}
              disabled={deleteArticle.isPending}
            >
              {deleteArticle.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        )}
      </div>

      <article className={styles.articleDetail__content}>
        <h1 className={styles.articleDetail__title}>{article.title}</h1>
        <ArticleMeta article={article} showAuthor={!isOwner} />
        <ArticleCollectionBadges
          articleId={articleId}
          onAddToCollection={isOwner ? () => setShowAddToCollectionModal(true) : undefined}
        />
        <ArticleViewer content={article.content} />
      </article>

      {/* Show recordings if there are any OR if user is owner */}
      {(article.recording_count > 0 || isOwner) && (
        <RecordingsList articleId={articleId} isOwner={isOwner} />
      )}

      <ArticleComments articleId={articleId} />

      {isOwner && (
        <>
          <ShareArticleModal
            articleId={articleId}
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
          />
          <AddArticleToCollectionsModal
            articleId={articleId}
            isOpen={showAddToCollectionModal}
            onClose={() => setShowAddToCollectionModal(false)}
          />
        </>
      )}
    </div>
  );
}
